import { sortBy } from "lodash";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetPayersByAccountQuery } from "store/queries/payers";
import { IBenefitSelectionRule } from "types/Account/Account";

import { BenefitSelectionRules } from "./BenefitSelectionRules";

/**
 * CLE-2792 review round 10 — the payer filters, bound to the ACCOUNT FORM, exactly as on main.
 *
 * 🚨 THIS IS THE PRODUCTION EDITOR AND IT MUST KEEP WORKING FOR EVERYBODY. The benefit-configuration
 * card took ownership of these rows, which is right for the person running the engine cutover and a
 * REGRESSION for everyone else: the card is hidden from them, so on deploy the only editor Ani has
 * would simply have vanished from the page. So the same list has two homes, chosen by who is
 * looking, and this one is the untouched path — same field array, same place on the page, same
 * "Update Hospital" save.
 *
 * The table and dialog underneath are shared: `BenefitSelectionRules` was made controlled in round
 * 1, so this supplies the value and the setter from the form and the card supplies them from its own
 * state. Only the OWNERSHIP differs, which is the only thing that should.
 */
export function BenefitSelectionRulesField() {
  const { control, watch } = useFormContext<any>();
  const { id: accountId } = useParams<{ id: string }>();

  const { data: payerByAccount, isLoading: loadingPayers } =
    useGetPayersByAccountQuery(accountId || "", { skip: !accountId });

  const payerOptions = sortBy(
    payerByAccount?.data?.map((payer: any) => ({
      label: payer.name,
      value: payer.id,
    })) || [],
    ["label"]
  );

  const { replace } = useFieldArray({
    control,
    name: "benefitSelectionRules",
  });

  // The field array's own `fields` snapshot does not track edits made through `replace`, so the
  // table reads the live form value instead.
  const rules: IBenefitSelectionRule[] = watch("benefitSelectionRules") || [];

  if (!accountId) {
    return (
      <span className="text-sm text-color-secondary">
        Payer filters can be added once the account has been created and its
        payers are known.
      </span>
    );
  }

  return (
    <BenefitSelectionRules
      value={rules}
      payerOptions={payerOptions}
      loadingPayers={loadingPayers}
      onChange={replace}
    />
  );
}
